import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Text,
  Container,
  Heading,
  VStack,
  HStack,
  Button,
  Card,
  CardBody,
  Spinner,
  IconButton,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Divider,
  Badge,
  useBreakpointValue,
  Flex,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react'
import { FiHeart, FiEye, FiEdit, FiTrash2, FiArrowLeft, FiMessageSquare } from 'react-icons/fi'
import { authState } from '../atoms/auth'
import {
  getCommunityDetail,
  updateCommunity,
  deleteCommunity,
  toggleCommunityLike,
  createComment,
  updateComment,
  deleteComment,
} from '../services/apiService'
import dayjs from 'dayjs'

interface Comment {
  id: number
  communityId: number
  userId: number
  username: string
  content: string
  parentId: number | null
  createdAt: string
  updatedAt: string | null
  replies: Comment[]
}

interface CommunityDetail {
  id: number
  userId: number
  username: string
  title: string
  content: string
  viewCount: number
  likeCount: number
  isLiked: boolean
  createdAt: string
  updatedAt: string | null
  comments: Comment[]
}

const CommunityDetail: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [auth] = useRecoilState(authState)
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const commentModal = useDisclosure()
  const deleteDialog = useDisclosure()
  const commentDeleteDialog = useDisclosure()
  const [deletingComment, setDeletingComment] = useState<Comment | null>(null)
  const isMobile = useBreakpointValue({ base: true, md: false })
  const cancelRef = React.useRef<HTMLButtonElement>(null)
  const commentCancelRef = React.useRef<HTMLButtonElement>(null)

  const [post, setPost] = useState<CommunityDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [formTitle, setFormTitle] = useState('')
  const [formContent, setFormContent] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [commentContent, setCommentContent] = useState('')
  const [replyingTo, setReplyingTo] = useState<number | null>(null)
  const [editingComment, setEditingComment] = useState<Comment | null>(null)

  const fetchPost = async () => {
    if (!id) return
    setLoading(true)
    try {
      const { data: res } = await getCommunityDetail(parseInt(id))
      if (res.statusCode === 200) {
        setPost(res.data)
        setFormTitle(res.data.title)
        setFormContent(res.data.content)
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('게시글을 불러오는데 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      navigate('/community')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPost()
  }, [id])

  const handleEdit = () => {
    if (!post || post.userId !== auth.id) {
      toast({
        title: t('권한 없음'),
        description: t('수정 권한이 없습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }
    setIsEditMode(true)
    onOpen()
  }

  const handleDeleteClick = () => {
    if (!post || post.userId !== auth.id) {
      toast({
        title: t('권한 없음'),
        description: t('삭제 권한이 없습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }
    deleteDialog.onOpen()
  }

  const handleDeleteConfirm = async () => {
    if (!post) return

    try {
      const { data: res } = await deleteCommunity(post.id)
      if (res.statusCode === 200) {
        toast({
          title: t('성공'),
          description: res.message,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        deleteDialog.onClose()
        navigate('/community')
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('삭제에 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const handleSubmit = async () => {
    if (!formTitle.trim() || !formContent.trim()) {
      toast({
        title: t('입력 오류'),
        description: t('제목과 내용을 모두 입력해주세요.'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    if (!post) return

    setSubmitting(true)
    try {
      const { data: res } = await updateCommunity(post.id, formTitle, formContent)
      if (res.statusCode === 200) {
        toast({
          title: t('성공'),
          description: res.message,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        onClose()
        fetchPost()
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('수정에 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleLike = async () => {
    if (!post) return

    if (!auth.id) {
      toast({
        title: t('로그인 필요'),
        description: t('좋아요를 누르려면 로그인이 필요합니다.'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      navigate('/signIn')
      return
    }

    try {
      const { data: res } = await toggleCommunityLike(post.id)
      if (res.statusCode === 200) {
        // 즉시 UI 업데이트
        setPost(prevPost =>
          prevPost
            ? { ...prevPost, likeCount: res.data.likeCount, isLiked: res.data.isLiked }
            : null
        )
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('좋아요 처리에 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const handleCommentSubmit = async () => {
    if (!commentContent.trim() || !post) {
      toast({
        title: t('입력 오류'),
        description: t('댓글 내용을 입력해주세요.'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    if (!auth.id) {
      toast({
        title: t('로그인 필요'),
        description: t('댓글을 작성하려면 로그인이 필요합니다.'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      navigate('/signIn')
      return
    }

    try {
      if (editingComment) {
        const { data: res } = await updateComment(editingComment.id, commentContent)
        if (res.statusCode === 200) {
          toast({
            title: t('성공'),
            description: res.message,
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
          setCommentContent('')
          setEditingComment(null)
          commentModal.onClose()
          fetchPost()
        }
      } else {
        const { data: res } = await createComment(post.id, commentContent, replyingTo || undefined)
        if (res.statusCode === 200) {
          toast({
            title: t('성공'),
            description: res.message,
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
          setCommentContent('')
          setReplyingTo(null)
          commentModal.onClose()
          fetchPost()
        }
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('댓글 작성에 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const handleReply = (commentId: number) => {
    if (!auth.id) {
      toast({
        title: t('로그인 필요'),
        description: t('댓글을 작성하려면 로그인이 필요합니다.'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      navigate('/signIn')
      return
    }
    setReplyingTo(commentId)
    setEditingComment(null)
    setCommentContent('')
    commentModal.onOpen()
  }

  const handleCommentEdit = (comment: Comment) => {
    if (comment.userId !== auth.id) {
      toast({
        title: t('권한 없음'),
        description: t('수정 권한이 없습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }
    setEditingComment(comment)
    setReplyingTo(null)
    setCommentContent(comment.content)
    commentModal.onOpen()
  }

  const handleCommentDeleteClick = (comment: Comment) => {
    if (comment.userId !== auth.id) {
      toast({
        title: t('권한 없음'),
        description: t('삭제 권한이 없습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }
    setDeletingComment(comment)
    commentDeleteDialog.onOpen()
  }

  const handleCommentDeleteConfirm = async () => {
    if (!deletingComment) return

    try {
      const { data: res } = await deleteComment(deletingComment.id)
      if (res.statusCode === 200) {
        toast({
          title: t('성공'),
          description: res.message,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        commentDeleteDialog.onClose()
        setDeletingComment(null)
        fetchPost()
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('삭제에 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const renderComment = (comment: Comment, depth: number = 0) => {
    return (
      <Box key={comment.id} pl={depth > 0 ? 6 : 0} borderLeft={depth > 0 ? '2px solid' : 'none'} borderColor="gray.200">
        <Card size="sm" mb={2}>
          <CardBody>
            <VStack align="stretch" spacing={2}>
              <HStack justify="space-between">
                <HStack>
                  <Text fontWeight="bold">{comment.username}</Text>
                  {depth > 0 && (
                    <Badge colorScheme="blue" size="sm">
                      {t('대댓글')}
                    </Badge>
                  )}
                </HStack>
                {comment.userId === auth.id && (
                  <HStack spacing={2}>
                    <IconButton
                      aria-label={t('수정')}
                      icon={<FiEdit />}
                      size="xs"
                      variant="ghost"
                      onClick={() => handleCommentEdit(comment)}
                    />
                    <IconButton
                      aria-label={t('삭제')}
                      icon={<FiTrash2 />}
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => handleCommentDeleteClick(comment)}
                    />
                  </HStack>
                )}
              </HStack>
              <Text>{comment.content}</Text>
              <HStack justify="space-between" fontSize="xs" color="gray.500">
                <Text>{dayjs(comment.createdAt).format('YYYY-MM-DD HH:mm')}</Text>
                {depth === 0 && (
                  <Button
                    size="xs"
                    variant="ghost"
                    leftIcon={<FiMessageSquare />}
                    onClick={() => handleReply(comment.id)}
                  >
                    {t('답글')}
                  </Button>
                )}
              </HStack>
            </VStack>
          </CardBody>
        </Card>
        {comment.replies && comment.replies.length > 0 && (
          <VStack align="stretch" spacing={2} mt={2}>
            {comment.replies.map((reply) => renderComment(reply, depth + 1))}
          </VStack>
        )}
      </Box>
    )
  }

  if (loading) {
    return (
      <DefaultLayout>
        <Container maxW="container.xl" py={10}>
          <Box textAlign="center" py={10}>
            <Spinner size="xl" />
          </Box>
        </Container>
      </DefaultLayout>
    )
  }

  if (!post) {
    return null
  }

  return (
    <DefaultLayout>
      <Container maxW="container.xl" py={10}>
        <VStack spacing={6} align="stretch">
          {/* 제목 - 맨 위 */}
          <Heading size={isMobile ? 'lg' : 'xl'}>{post.title}</Heading>

          {/* 뒤로가기 및 수정/삭제 버튼 */}
          <Flex
            direction={isMobile ? 'column' : 'row'}
            gap={isMobile ? 3 : 0}
            align={isMobile ? 'stretch' : 'center'}
            justify="space-between"
          >
            <HStack spacing={2}>
              <IconButton
                aria-label={t('뒤로가기')}
                icon={<FiArrowLeft />}
                onClick={() => navigate('/community')}
                size={isMobile ? 'md' : 'sm'}
              />
              {isMobile && (
                <Text fontSize="sm" color="gray.500">
                  {t('커뮤니티')}
                </Text>
              )}
            </HStack>
            {post.userId === auth.id && (
              <HStack spacing={2} justify={isMobile ? 'flex-end' : 'flex-start'}>
                <Button
                  leftIcon={<FiEdit />}
                  size={isMobile ? 'md' : 'sm'}
                  onClick={handleEdit}
                  flex={isMobile ? 1 : 'none'}
                >
                  {t('수정')}
                </Button>
                <Button
                  leftIcon={<FiTrash2 />}
                  size={isMobile ? 'md' : 'sm'}
                  colorScheme="red"
                  variant="outline"
                  onClick={handleDeleteClick}
                  flex={isMobile ? 1 : 'none'}
                >
                  {t('삭제')}
                </Button>
              </HStack>
            )}
          </Flex>

          <Card>
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between" fontSize="sm" color="gray.500">
                  <HStack spacing={4}>
                    <Text>{post.username}</Text>
                    <Text>{dayjs(post.createdAt).format('YYYY-MM-DD HH:mm')}</Text>
                  </HStack>
                  <HStack spacing={4}>
                    <HStack spacing={1}>
                      <FiEye />
                      <Text>{post.viewCount}</Text>
                    </HStack>
                    <HStack
                      spacing={1}
                      onClick={handleLike}
                      cursor="pointer"
                      color={post.isLiked ? 'brand.600' : 'gray.500'}
                      _hover={{ color: 'brand.600' }}
                    >
                      <FiHeart fill={post.isLiked ? 'currentColor' : 'none'} />
                      <Text>{post.likeCount}</Text>
                    </HStack>
                  </HStack>
                </HStack>
                <Divider />
                <Text whiteSpace="pre-wrap">{post.content}</Text>
              </VStack>
            </CardBody>
          </Card>

          <Divider />

          <VStack align="stretch" spacing={4}>
            <HStack justify="space-between">
              <Heading size="md">{t('댓글')} ({post.comments.length})</Heading>
              <Button
                leftIcon={<FiMessageSquare />}
                colorScheme="brand"
                size="sm"
                onClick={() => {
                  setReplyingTo(null)
                  setEditingComment(null)
                  setCommentContent('')
                  commentModal.onOpen()
                }}
              >
                {t('댓글 작성')}
              </Button>
            </HStack>

            {post.comments.length === 0 ? (
              <Box textAlign="center" py={8}>
                <Text color="gray.500">{t('댓글이 없습니다.')}</Text>
              </Box>
            ) : (
              <VStack align="stretch" spacing={4}>
                {post.comments.map((comment) => renderComment(comment))}
              </VStack>
            )}
          </VStack>
        </VStack>
      </Container>

      {/* 게시글 수정 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('게시글 수정')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>{t('제목')}</FormLabel>
                <Input
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder={t('제목을 입력하세요')}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('내용')}</FormLabel>
                <Textarea
                  value={formContent}
                  onChange={(e) => setFormContent(e.target.value)}
                  placeholder={t('내용을 입력하세요')}
                  rows={10}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t('취소')}
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleSubmit}
              isLoading={submitting}
            >
              {t('수정')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 댓글 작성/수정 모달 */}
      <Modal isOpen={commentModal.isOpen} onClose={commentModal.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingComment ? t('댓글 수정') : replyingTo ? t('대댓글 작성') : t('댓글 작성')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>{t('댓글 내용')}</FormLabel>
              <Textarea
                value={commentContent}
                onChange={(e) => setCommentContent(e.target.value)}
                placeholder={t('댓글 내용을 입력하세요')}
                rows={5}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={commentModal.onClose}>
              {t('취소')}
            </Button>
            <Button colorScheme="brand" onClick={handleCommentSubmit}>
              {editingComment ? t('수정') : t('작성')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 게시글 삭제 확인 다이얼로그 */}
      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={deleteDialog.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('삭제 확인')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t('정말 삭제하시겠습니까?')}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={deleteDialog.onClose}>
                {t('취소')}
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                {t('삭제')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* 댓글 삭제 확인 다이얼로그 */}
      <AlertDialog
        isOpen={commentDeleteDialog.isOpen}
        leastDestructiveRef={commentCancelRef}
        onClose={commentDeleteDialog.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('삭제 확인')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t('정말 삭제하시겠습니까?')}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={commentCancelRef} onClick={commentDeleteDialog.onClose}>
                {t('취소')}
              </Button>
              <Button colorScheme="red" onClick={handleCommentDeleteConfirm} ml={3}>
                {t('삭제')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </DefaultLayout>
  )
}

export default CommunityDetail

